import { useEffect, useMemo, useRef, useState } from 'react';
import { demeterNewsApi } from '../../../Apis/Apis';
import {
    DemeterNewsArticleModel,
    DemeterNewsArticleStatus,
    GetDemeterNewsArticleResponse,
    ListDemeterNewsArticleRatingsResponse,
    ListDemeterNewsArticlesResponse,
    RatingSource,
    SortDirection,
    UpdateDemeterNewsArticleStatusRequest,
} from '../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import CardSkeleton from '../../Components/Layout/CardSkeleton';
import useLanguage from '../../Services/Language/useLanguageHook';
import { ACTION_STATUS_MAP, ArticleAction, getAvailableActions, parseFieldConfidences, STATUS_LABELS } from './articleActions';
import styles from './ArtificialIntelligence.module.scss';

interface IArticleDetailProps {
    article: DemeterNewsArticleModel;
    onBack: () => void;
}

const ArticleDetail = ({ article, onBack }: IArticleDetailProps) => {
    const [translations] = useLanguage();
    const [currentStatus, setCurrentStatus] = useState(article.articleStatus);
    const pendingStatusReference = useRef<DemeterNewsArticleStatus | null>(null);

    const [loading, refresh, detail, error] = useApiWithoutAutoExecute<void, GetDemeterNewsArticleResponse>(
        () => demeterNewsApi.getDemeterNewsArticle(article.demeterNewsArticleGuid),
        { errorMessage: 'Failed to load article', showResponseErrorMessage: true },
    );

    const [, refreshRatings, ratingsResponse] = useApiWithoutAutoExecute<void, ListDemeterNewsArticleRatingsResponse>(
        () => demeterNewsApi.listDemeterNewsArticleRatings(article.demeterNewsArticleGuid),
        { errorMessage: 'Failed to load ratings', showResponseErrorMessage: true },
    );

    const [, refreshArticleStatus, articleStatusResponse] = useApiWithoutAutoExecute<void, ListDemeterNewsArticlesResponse>(
        () => demeterNewsApi.listDemeterNewsArticles(undefined, undefined, undefined, undefined, undefined, SortDirection.Desc, 1000, 0),
        { errorMessage: 'Failed to refresh article status' },
    );

    const ratings = useMemo(() => ratingsResponse?.rows ?? [], [ratingsResponse]);

    useEffect(() => {
        const matchingArticle = articleStatusResponse?.rows?.find((row) => row.demeterNewsArticleGuid === article.demeterNewsArticleGuid);
        if (matchingArticle) {
            setCurrentStatus(matchingArticle.articleStatus);
        }
    }, [articleStatusResponse]);

    const [statusUpdating, updateStatus] = useApiWithoutAutoExecute<UpdateDemeterNewsArticleStatusRequest, object>(
        (request) => {
            if (!request) {
                return null;
            }
            return demeterNewsApi.updateDemeterNewsArticleStatus(request.demeterNewsArticleGuid, request);
        },
        { successMessage: 'Status updated', errorMessage: 'Failed to update status' },
    );

    const [ratingLoading, triggerRate] = useApiWithoutAutoExecute<void, object>(
        () => demeterNewsApi.runDemeterNewsArticleRating(article.demeterNewsArticleGuid),
        { successMessage: 'Rating complete', errorMessage: 'Failed to rate article' },
    );

    const [deleteLoading, triggerDelete] = useApiWithoutAutoExecute<void, object>(
        () => demeterNewsApi.deleteDemeterNewsArticle(article.demeterNewsArticleGuid),
        { successMessage: 'Article deleted', errorMessage: 'Failed to delete article' },
    );

    useEffect(() => {
        refresh();
        refreshRatings();
    }, []);

    useEffect(() => {
        if (!statusUpdating && pendingStatusReference.current) {
            setCurrentStatus(pendingStatusReference.current);
            pendingStatusReference.current = null;
            refreshRatings();
        }
    }, [statusUpdating]);

    const previousRatingLoadingReference = useRef(false);

    useEffect(() => {
        if (previousRatingLoadingReference.current && !ratingLoading) {
            refreshArticleStatus();
            refreshRatings();
        }
        previousRatingLoadingReference.current = ratingLoading;
    });

    const previousDeleteLoadingReference = useRef(false);

    useEffect(() => {
        if (previousDeleteLoadingReference.current && !deleteLoading) {
            onBack();
        }
        previousDeleteLoadingReference.current = deleteLoading;
    });

    const updating = statusUpdating || ratingLoading || deleteLoading;

    const maxConfidence = useMemo(() => {
        if (ratings.length === 0) {
            return null;
        }
        const best = Math.max(...ratings.map((r) => r.confidenceScore ?? 0));
        return best > 0 ? best : null;
    }, [ratings]);

    const aiRatings = useMemo(() => ratings.filter((r) => r.ratingSource === RatingSource.ArtificialIntelligence), [ratings]);
    const preFilterRatings = useMemo(() => ratings.filter((r) => r.ratingSource === RatingSource.PreFilter), [ratings]);

    const published = useMemo(() => (article.articleDate ? new Date(article.articleDate).toLocaleString() : '-'), [article.articleDate]);
    const availableActions = useMemo(() => getAvailableActions(currentStatus), [currentStatus]);

    const handleStatusChange = (targetStatus: DemeterNewsArticleStatus) => {
        pendingStatusReference.current = targetStatus;
        updateStatus({
            demeterNewsArticleGuid: article.demeterNewsArticleGuid,
            targetStatus,
        });
    };

    const handleRate = () => {
        triggerRate();
    };

    const handleDelete = () => {
        triggerDelete();
    };

    const getActionHandler = (action: ArticleAction) => {
        if (action === ArticleAction.Rate) {
            return handleRate;
        }
        if (action === ArticleAction.Delete) {
            return handleDelete;
        }
        return () => handleStatusChange(ACTION_STATUS_MAP[action]!);
    };

    return (
        <div className={styles.article_detail_page}>
            <div className={styles.article_detail_header}>
                <LinkButton
                    title={translations.actions.back}
                    type={LinkButtonType.White}
                    onClick={onBack}
                    testId="BackToArticlesButton"
                    track={{ eventCategory: 'AiNewsRating', eventAction: 'BackClicked' }}
                />
                <div className={styles.article_detail_actions}>
                    {availableActions.map((action) => (
                        <LinkButton
                            key={action}
                            title={action}
                            type={action === ArticleAction.Publish ? LinkButtonType.Blue : LinkButtonType.White}
                            onClick={getActionHandler(action)}
                            disabled={updating}
                            testId={`ArticleDetail${action}Button`}
                            track={{ eventCategory: 'AiNewsRating', eventAction: `Article${action}` }}
                        />
                    ))}
                </div>
            </div>

            <h3 className={styles.article_detail_title}>{article.articleName ?? article.demeterNewsArticleGuid}</h3>

            <div className={styles.article_detail_meta}>
                <span>
                    {translations.words.source}: {article.articleSource ?? '-'}
                </span>
                <span>
                    {translations.words.date}: {published}
                </span>
                <span>
                    {translations.words.status}: {STATUS_LABELS[currentStatus] ?? currentStatus}
                </span>
                {maxConfidence !== null && (
                    <span>
                        {translations.words.confidence}: {maxConfidence.toFixed(2)}
                    </span>
                )}
            </div>

            {loading && <CardSkeleton minimumHeight={200} />}
            {error && <div className={styles.error_message}>{error}</div>}

            {!loading && !error && (
                <pre className={styles.article_detail_content} data-testid="ArticleDetailContent">
                    {detail?.content ?? ''}
                </pre>
            )}

            {preFilterRatings.length > 0 && (
                <div className={styles.article_detail_ratings_section}>
                    <h4 className={styles.article_detail_section_title}>PreFilter</h4>
                    {preFilterRatings.map((r) => (
                        <div key={r.demeterNewsArticleRatingGuid} className={styles.article_detail_rating_card}>
                            <span>
                                {translations.words.confidence}: {r.confidenceScore?.toFixed(2) ?? '-'}
                            </span>
                            {r.summary && (
                                <span>
                                    {translations.words.summary}: {r.summary}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {aiRatings.length > 0 && (
                <div className={styles.article_detail_ratings_section}>
                    <h4 className={styles.article_detail_section_title}>AI Ratings</h4>
                    {aiRatings.map((r) => {
                        const fc = parseFieldConfidences(r.comments);
                        return (
                            <div key={r.demeterNewsArticleRatingGuid} className={styles.article_detail_rating_card}>
                                <span>
                                    {translations.words.commodity}: {r.commodity ?? '-'}
                                </span>
                                <span>
                                    {translations.words.geography}: {r.geography ?? '-'}
                                </span>
                                <span>
                                    {translations.words.timeFrame}: {r.timeFrame ?? '-'}
                                </span>
                                <span>
                                    {translations.words.supply}: {r.fundamentalSupply ?? '-'}
                                    {fc.supply !== null ? ` (${fc.supply.toFixed(2)})` : ''}
                                </span>
                                <span>
                                    {translations.words.demand}: {r.fundamentalDemand ?? '-'}
                                    {fc.demand !== null ? ` (${fc.demand.toFixed(2)})` : ''}
                                </span>
                                <span>
                                    {translations.aiNewsRating.priceSentiment}: {r.fundamentalSentiment ?? '-'}
                                    {fc.sentiment !== null ? ` (${fc.sentiment.toFixed(2)})` : ''}
                                </span>
                                <span>
                                    {translations.words.confidence}: {r.confidenceScore?.toFixed(2) ?? '-'}
                                </span>
                                {r.summary && <span className={styles.article_detail_rating_summary}>{r.summary}</span>}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* TODO(AI-NEWS): Human-in-the-loop rating audit UI */}
        </div>
    );
};

export default ArticleDetail;
